import React, { useEffect, useMemo, useState } from "react";
import "./App.css";
import Navbar from "./components/Navbar/Navbar";
import { Route, Routes, useSearchParams } from "react-router";
import Homepage from "./components/Home/Homepage";
import {
  Configuration,
  CustomerContactData,
  defaultConfig,
} from "./components/Types/Configuration";
import CarItem from "./components/Cars/CarItem";
import axios from "axios";
import { apiBaseUrl, imageServerUrl, imageSizes } from "./constants/config";
import CarList from "./components/Cars/CarList";
import Footer from "./components/Utilities/Footer";
import ConsentBanner from "./consent/ConsentBanner";
import CookiePolicyPage from "./consent/CookiePolicyPage";
import { declaredVendors, siteTags } from "./consent/consent";

function App() {
  const [primaryColor, setPrimaryColor] = useState("#3F81F3");
  const [configObject, setConfigObject] =
    useState<Configuration>(defaultConfig);
  const [contactData, setContactData] = useState<CustomerContactData>(
    {} as CustomerContactData,
  );
  const [secondaryColor, setSecondaryColor] = useState<string>("#FFFFFF");
  const [loading, setLoading] = useState<boolean>(false);
  const [configError, setConfigError] = useState<boolean>(false);
  const searchParams = useSearchParams();
  // Captured once: the admin opens the preview at /?preview=true and then
  // navigates client-side, which drops the parameter. The preview must keep
  // reading the draft config and must never persist a consent choice.
  const [isPreviewMode] = useState(
    () => searchParams[0].get("preview") === "true",
  );

  const urlForConfig = isPreviewMode
    ? `${apiBaseUrl}/customer-website/preview`
    : `${apiBaseUrl}/customer-website`;

  useEffect(() => {
    // Set the CSS custom property on the document root
    document.documentElement.style.setProperty("--primary-color", primaryColor);
  }, [primaryColor]);
  useEffect(() => {
    // Set the CSS custom property on the document root
    document.documentElement.style.setProperty(
      "--secondary-color",
      secondaryColor,
    );
  }, [secondaryColor]);

  useEffect(() => {
    fetchConfiguration();
    fetchContactData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (configObject.logoImageId) {
      // Use the square favicon transform, not the rectangular navbar thumbnail,
      // so the tab icon isn't cropped/distorted. The server already injects this
      // into the served HTML; this keeps preview mode and SPA navigation in sync.
      const faviconUrl = `${imageServerUrl}/image/${imageSizes.favicon}/${configObject.logoImageId}`;

      // Find existing favicon link or create new one
      let link: HTMLLinkElement | null =
        document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = faviconUrl;
      link.setAttribute("sizes", "256x256");

      // Also update apple-touch-icon for iOS
      let appleTouchIcon: HTMLLinkElement | null = document.querySelector(
        "link[rel~='apple-touch-icon']",
      );
      if (!appleTouchIcon) {
        appleTouchIcon = document.createElement("link");
        appleTouchIcon.rel = "apple-touch-icon";
        document.head.appendChild(appleTouchIcon);
      }
      appleTouchIcon.href = faviconUrl;
    }
  }, [configObject.logoImageId]);

  useEffect(() => {
    if (contactData.name) {
      document.title = contactData.name;
    }
  }, [contactData.name]);

  // The API delivers homepage as a JSON string; fall back to the empty
  // default structure if it is missing or malformed
  const parseHomepage = (
    rawHomepage: Configuration["homepage"] | string | undefined,
  ): Configuration["homepage"] => {
    if (typeof rawHomepage === "string") {
      try {
        return JSON.parse(rawHomepage) as Configuration["homepage"];
      } catch (error) {
        console.error("Error parsing homepage configuration:", error);
        return defaultConfig.homepage;
      }
    }
    return rawHomepage || defaultConfig.homepage;
  };

  const fetchConfiguration = async () => {
    setLoading(true);
    setConfigError(false);
    try {
      const response = await axios.get<Configuration>(urlForConfig);
      const rawConfig = response.data;
      const completeConfig: Configuration = {
        ...rawConfig,
        homepage: parseHomepage(rawConfig?.homepage),
      };
      setConfigObject(completeConfig);
      if (rawConfig?.primaryColor) {
        setPrimaryColor(rawConfig.primaryColor);
      }
      if (rawConfig?.secondaryColor) {
        setSecondaryColor(rawConfig.secondaryColor);
      }
    } catch (error) {
      console.error("Error fetching configuration:", error);
      setConfigError(true);
    } finally {
      setLoading(false);
    }
  };
  const fetchContactData = async () => {
    try {
      const response = await axios.get<CustomerContactData>(
        `${apiBaseUrl}/contact-data`,
      );
      setContactData(response.data);
    } catch (error) {
      console.error("Error fetching contact data:", error);
    }
  };

  // The server injects window.__SITE_TAGS__ only when it actually emitted the
  // Consent Mode bootstrap and the loader, so it is the source of truth: a
  // container id the server rejected must not produce a banner with nothing
  // behind it. The admin preview gets no bootstrap, so the saved settings stand
  // in there to let the dealer see the banner they are configuring.
  const serverTags = siteTags();
  const gtmActive = isPreviewMode
    ? Boolean(configObject.tagSettings?.gtmContainerId)
    : Boolean(serverTags?.gtmId);
  // Memoised: a fresh object each render would retrigger the banner's effect,
  // which sets state, which renders again.
  const policyVendors = declaredVendors(configObject.cookiePolicy);
  const declared = useMemo(
    () => serverTags?.vendors ?? policyVendors,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [serverTags?.gtmId, policyVendors.analytics.join(","), policyVendors.marketing.join(",")],
  );

  if (loading) {
    return (
      <>
        <Navbar logoUrl={configObject.logoImageId || ""} loading={loading} />
        <div className="app-loading-container">
          <div className="loading"></div>
        </div>
      </>
    );
  }
  if (configError) {
    return (
      <>
        <Navbar logoUrl="" loading={false} />
        <div className="app-error-container">
          <p>Sivun lataaminen epäonnistui. Yritä hetken kuluttua uudelleen.</p>
          <button onClick={fetchConfiguration}>Yritä uudelleen</button>
        </div>
      </>
    );
  }
  return (
    <>
      <Navbar logoUrl={configObject.logoImageId || ""} loading={loading} />
      <div className="App">
        <div className="fullpage-container">
          <Routes>
            <Route
              path="/"
              element={
                <Homepage
                  homepageConfig={configObject.homepage}
                  loading={loading}
                  contactData={contactData}
                />
              }
            />
            <Route path="/cars" element={<CarList />} />
            <Route path="/car/:id" element={<CarItem contactDataUser={contactData?.contactUsers?.[0]}/>} />
            <Route
              path="/evastekaytanto"
              element={
                <CookiePolicyPage
                  policy={configObject.cookiePolicy}
                  contactData={contactData}
                  updatedAt={configObject.UpdatedAt}
                  gtmActive={gtmActive}
                />
              }
            />
          </Routes>
        </div>
        <Footer footerData={configObject} contactData={contactData} showConsentSettings={gtmActive} />
        <ConsentBanner gtmActive={gtmActive} declared={declared} isPreview={isPreviewMode} />
      </div>
    </>
  );
}

export default App;
